import { applyGermanize, germanize } from './germanize.js';
import { useFocusTrap, useInstallPrompt, useIsMobile } from './hooks.js';
import { accent, accentBorder, accentDim, bg, border, borderLight, surface, surface2, textDim, textMuted } from './tokens.js';

const { useState, useEffect } = React;

// ─── Header ──────────────────────────────────────────────────────────────
export function Header({ onLogo, onAbout, onReflexion, onSearch, onMein, userName }) {
  const mobile = useIsMobile();
  const install = useInstallPrompt();
  const linkBtn = {
    background: "none", border: "none", color: textMuted,
    cursor: "pointer", fontFamily: "inherit", fontSize: 13, padding: "4px 6px"
  };
  return (
    <header style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "15px 18px",
      borderBottom: `1px solid ${border}`,
      background: "rgba(38,39,41,0.97)", position: "sticky", top: 0, zIndex: 100
    }}>
      <button onClick={onLogo} aria-label="Obscura, zur Startseite"
              style={{
                background: "none", border: "none", padding: 0, cursor: "pointer",
                display: "flex", alignItems: "center", gap: 10, flexShrink: 0
              }}>
        <svg width="20" height="20" viewBox="0 0 200 200" aria-hidden="true" style={{flexShrink: 0}}>
          <circle cx="100" cy="100" r="80" fill="none" stroke={accent} strokeWidth="9"/>
          <circle cx="100" cy="100" r="50" fill="none" stroke={accent} strokeWidth="6" opacity="0.6"/>
          <circle cx="130" cy="100" r="13" fill={accent}/>
        </svg>
        <span style={{fontWeight: 800, fontSize: "0.95rem", letterSpacing: "-0.02em", color: "#e8e6e3", whiteSpace: "nowrap"}}>Obscura</span>
      </button>
      {!mobile && (
        <span style={{fontSize: "0.65rem", color: textDim, marginLeft: 4, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1}}>
          {applyGermanize("Muster sehen, die verborgen wirken.")}
        </span>
      )}
      {mobile && <span style={{flex: 1}} />}
      {!mobile && install.canInstall && (
        <button onClick={install.promptInstall} aria-label="Als App installieren" title="Als App installieren" style={{
          background: "none", border: "none", color: textMuted,
          cursor: "pointer", padding: 6, display: "flex", alignItems: "center"
        }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M8 2v7M8 9l3-3M8 9L5 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M3 12.5h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
          </svg>
        </button>
      )}
      {onSearch && (
        <button onClick={onSearch} aria-label="Suche oeffnen" title="Suche (/)" style={{
          background: "none", border: "none", color: textMuted,
          cursor: "pointer", padding: 6, display: "flex", alignItems: "center"
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
        </button>
      )}
      {onReflexion && (
        <button onClick={onReflexion} style={linkBtn}>
          {applyGermanize("Reflexionsfragen")}
        </button>
      )}
      <button onClick={onAbout} style={linkBtn}>
        {applyGermanize("Ueber")}
      </button>
      {onMein && (
        <button onClick={onMein} aria-label="Mein Bereich" title="Mein Bereich" style={{
          background: userName && userName.trim() ? accentDim : "rgba(255,255,255,0.06)",
          border: `1px solid ${userName && userName.trim() ? accentBorder : border}`,
          borderRadius: "50%", width: 28, height: 28, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, padding: 0
        }}>
          {userName && userName.trim() ? (
            <span style={{fontSize: 13, fontWeight: 700, color: accent, lineHeight: 1}}>{userName.trim()[0].toUpperCase()}</span>
          ) : (
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="5.5" r="2.5" stroke={textDim} strokeWidth="1.4"/>
              <path d="M2.5 13.5c0-3 2.5-5 5.5-5s5.5 2 5.5 5" stroke={textDim} strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
          )}
        </button>
      )}
    </header>
  );
}

// ─── GroupNav ────────────────────────────────────────────────────────────
export function GroupNav({ currentPatternId, onSelect }) {
  const mobile = useIsMobile();
  const sortedGroups = Object.entries(GROUPS).sort((a, b) => a[1].order - b[1].order);
  const currentPattern = currentPatternId ? PATTERNS[currentPatternId] : null;
  const activeGroup = currentPattern ? currentPattern.group : sortedGroups[0][0];

  // Ebene 2: Muster-Pills klappen auf/zu. Offen beim Betreten einer Gruppe
  // (Mount / Kategorie-Klick), zu nach Muster-Wahl. Kategorie-Klick oeffnet nur
  // (navigiert nicht) -> unverbindliches Stoebern in einer fremden Gruppe.
  const [openGroup, setOpenGroup] = React.useState(activeGroup);
  const firstRender = React.useRef(true);
  React.useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    setOpenGroup(null);          // Muster-Wechsel (Wahl/Suche/Deep-Link) schliesst die Liste
  }, [currentPatternId]);

  const isOpen = openGroup !== null;
  // Mobile: aufklappende Pills (spart Platz). Desktop: Pills permanent (voller Ueberblick).
  const shownGroup = mobile ? (isOpen ? openGroup : activeGroup) : activeGroup;
  const members = (GROUPS[shownGroup] && GROUPS[shownGroup].members) || [];

  function chooseMuster(id) { onSelect(id); setOpenGroup(null); }
  function tabClick(key) {
    if (mobile) setOpenGroup(prev => prev === key ? null : key);
    else { const m = GROUPS[key].members; if (m && m[0]) onSelect(m[0]); }
  }

  return (
    <nav style={{borderBottom: `1px solid ${border}`, background: bg}}>
      <div style={{maxWidth: 960, margin: "0 auto"}}>
      {/* Ebene 1: Kategorien-Tabs — Klick oeffnet/schliesst die Gruppe (kein Navigieren) */}
      <div style={{ display: "flex", padding: mobile ? "0 8px" : "0 16px" }}>
        {sortedGroups.map(([key, g]) => {
          const isMarked = key === shownGroup;
          const label = mobile ? g.label.split(" & ")[0] : g.label;
          return (
            <button key={key}
                    onClick={() => tabClick(key)}
                    style={{
                      flex: "1 1 0", minWidth: 0,
                      padding: mobile ? "9px 4px" : "9px 0",
                      background: "none", border: "none",
                      color: isMarked ? accent : textMuted,
                      borderBottom: `2px solid ${isMarked ? accent : "transparent"}`,
                      cursor: "pointer", fontFamily: "inherit",
                      fontSize: mobile ? "0.6rem" : "0.67rem", letterSpacing: "0.02em",
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      textAlign: "center"
                    }}>
              {applyGermanize(label)}
            </button>
          );
        })}
      </div>

      {mobile ? (
      <>
      {/* Mobile, Ebene 2 zugeklappt: aktives Muster-Pill — faehrt gegenlaeufig zu */}
      <div style={{display: "grid", gridTemplateRows: isOpen ? "0fr" : "1fr", transition: "grid-template-rows 0.28s ease"}}>
        <div style={{overflow: "hidden"}}>
          {currentPattern && (
            <div style={{display: "flex", justifyContent: "center", padding: mobile ? "10px 12px" : "12px 16px"}}>
              <button onClick={() => setOpenGroup(activeGroup)} aria-expanded={isOpen}
                      style={{
                        display: "flex", alignItems: "center", gap: 9,
                        padding: "6px 14px", borderRadius: 999,
                        background: surface2, border: `1px solid ${borderLight}`,
                        color: "#e8e6e3", fontSize: 12, fontFamily: "inherit", cursor: "pointer"
                      }}>
                {applyGermanize(currentPattern.title)}
                <span style={{color: textDim, fontSize: 11.5}}>{(GROUPS[activeGroup].members || []).length} Muster</span>
                <span style={{color: textDim, fontSize: 11}}>▾</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Ebene 2 aufgeklappt: Pills-Reihe der Gruppe — sanftes Auffahren (grid-rows) */}
      <div style={{display: "grid", gridTemplateRows: isOpen ? "1fr" : "0fr", transition: "grid-template-rows 0.28s ease"}}>
        <div style={{overflow: "hidden"}}>
          <div style={{display: "flex", gap: 5, padding: mobile ? "10px 12px" : "12px 16px", flexWrap: "wrap", justifyContent: "center"}}>
            {members.map(id => {
              const p = PATTERNS[id];
              if (!p) return null;
              const isActive = id === currentPatternId;
              return (
                <button key={id}
                        onClick={() => chooseMuster(id)}
                        style={{
                          display: "flex", alignItems: "center", gap: 7,
                          padding: "5px 12px", borderRadius: 999,
                          background: isActive ? accentDim : "transparent",
                          color: isActive ? accent : textMuted,
                          border: `1px solid ${isActive ? accentBorder : border}`,
                          cursor: "pointer", fontFamily: "inherit",
                          fontSize: "0.68rem", whiteSpace: "nowrap"
                        }}>
                  {applyGermanize(p.title)}
                  {isActive && <span style={{opacity: 0.7, fontSize: 11}}>▴</span>}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      </>
      ) : (
        /* Desktop: alle Muster-Pills permanent sichtbar (voller Ueberblick) */
        <div style={{display: "flex", gap: 5, padding: "12px 16px", flexWrap: "wrap", justifyContent: "center"}}>
          {members.map(id => {
            const p = PATTERNS[id];
            if (!p) return null;
            const isActive = id === currentPatternId;
            return (
              <button key={id} onClick={() => onSelect(id)}
                      style={{padding: "5px 12px", borderRadius: 999, background: isActive ? accentDim : "transparent", color: isActive ? accent : textMuted, border: `1px solid ${isActive ? accentBorder : border}`, cursor: "pointer", fontFamily: "inherit", fontSize: "0.68rem", whiteSpace: "nowrap"}}>
                {applyGermanize(p.title)}
              </button>
            );
          })}
        </div>
      )}
      </div>
    </nav>
  );
}

// ─── SearchOverlay ───────────────────────────────────────────────────────
export function SearchOverlay({ onClose, onPick }) {
  const mobile = useIsMobile();
  const [q, setQ] = useState("");
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = React.useRef(null);
  const trapRef = useFocusTrap();

  useEffect(() => { if (inputRef.current) inputRef.current.focus(); }, []);

  const index = React.useMemo(() => {
    const entries = [];
    for (const p of Object.values(PATTERNS)) {
      entries.push({ patternId: p.id, title: p.title, field: "Titel", snippet: p.title, hay: germanize(p.title).toLowerCase() });
      if (p.titleEm) entries.push({ patternId: p.id, title: p.title, field: "Untertitel", snippet: p.titleEm, hay: germanize(p.titleEm).toLowerCase() });
      entries.push({ patternId: p.id, title: p.title, field: "Ziel", snippet: p.goal, hay: germanize(p.goal).toLowerCase() });
      (p.detection || []).forEach(d => entries.push({ patternId: p.id, title: p.title, field: "Erkennungsmerkmal", snippet: d, hay: germanize(d).toLowerCase() }));
      (p.reaction || []).forEach(r => entries.push({ patternId: p.id, title: p.title, field: "Reaktion", snippet: r, hay: germanize(r).toLowerCase() }));
    }
    return entries;
  }, []);

  const query = germanize(q.trim()).toLowerCase();
  const results = query.length < 2 ? [] : index.filter(e => e.hay.includes(query)).slice(0, 30);

  useEffect(() => { setSelectedIdx(0); }, [q]);

  useEffect(() => {
    const h = (e) => {
      if (results.length === 0) return;
      if (e.key === "ArrowDown") { e.preventDefault(); setSelectedIdx(i => Math.min(i + 1, results.length - 1)); }
      if (e.key === "ArrowUp")   { e.preventDefault(); setSelectedIdx(i => Math.max(i - 1, 0)); }
      if (e.key === "Enter")     { e.preventDefault(); const r = results[selectedIdx]; if (r) onPick(r.patternId); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [results, selectedIdx]);

  const panelStyle = mobile
    ? { position: "fixed", inset: 0, background: bg, display: "flex", flexDirection: "column", zIndex: 70 }
    : {
        position: "fixed", top: 80, left: "50%",
        transform: "translateX(-50%)",
        width: "calc(100% - 40px)", maxWidth: 640, maxHeight: "75vh",
        background: bg, borderRadius: 12,
        display: "flex", flexDirection: "column", zIndex: 71,
        boxShadow: "0 16px 48px rgba(0,0,0,0.6)",
        border: `1px solid ${border}`
      };

  return (
    <>
      {!mobile && (
        <div onClick={onClose}
             style={{position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 70}} />
      )}
      <div ref={trapRef} style={panelStyle} role="dialog" aria-modal="true" aria-label="Suche" tabIndex={-1}>
        {/* Input row */}
        <div style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "14px 18px", borderBottom: `1px solid ${border}`
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={textDim} strokeWidth="2">
            <circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input ref={inputRef} type="text"
                 value={q} onChange={e => setQ(e.target.value)}
                 placeholder={applyGermanize("Suchen — Titel, Ziel, Erkennungsmerkmal, Reaktion")}
                 style={{
                   flex: 1, background: "transparent", border: "none", outline: "none",
                   color: "#e8e6e3", fontFamily: "inherit", fontSize: 15, caretColor: accent
                 }} />
          <button onClick={onClose} aria-label="Schliessen" style={{
            background: "none", border: "none", color: textMuted,
            fontSize: 22, cursor: "pointer", padding: 0, lineHeight: 1
          }}>×</button>
        </div>

        {/* Results */}
        <div style={{flex: 1, overflowY: "auto"}}>
          {q.trim().length > 0 && query.length < 2 && (
            <div style={{padding: "16px 20px", color: textDim, fontSize: 13, fontFamily: "Georgia, serif", fontStyle: "italic"}}>
              {applyGermanize("Mindestens 2 Zeichen.")}
            </div>
          )}
          {query.length >= 2 && results.length === 0 && (
            <div style={{padding: "16px 20px", color: textDim, fontSize: 13, fontFamily: "Georgia, serif", fontStyle: "italic"}}>
              {applyGermanize("Keine Treffer.")}
            </div>
          )}
          {query.length < 2 && q.trim().length === 0 && (
            <div style={{padding: "16px 20px", color: textDim, fontSize: 12, fontFamily: "Georgia, serif", fontStyle: "italic"}}>
              {applyGermanize("Tippen, um zu suchen. Pfeile + Enter zum Auswaehlen, ESC zum Schliessen.")}
            </div>
          )}
          {results.map((r, i) => (
            <button key={i}
                    onClick={() => onPick(r.patternId)}
                    onMouseEnter={() => setSelectedIdx(i)}
                    style={{
                      width: "100%", textAlign: "left", padding: "12px 18px",
                      background: i === selectedIdx ? surface : "transparent",
                      border: "none", borderBottom: `1px solid ${border}`,
                      cursor: "pointer", fontFamily: "inherit",
                      display: "block"
                    }}>
              <div style={{display: "flex", alignItems: "baseline", gap: 10}}>
                <span style={{color: accent, fontSize: 14, flex: 1}}>{applyGermanize(r.title)}</span>
                <span style={{color: textDim, fontSize: 10, textTransform: "uppercase", letterSpacing: 1, flexShrink: 0}}>
                  {applyGermanize(r.field)}
                </span>
              </div>
              {r.field !== "Titel" && (
                <div style={{
                  color: textMuted, fontFamily: "Georgia, serif",
                  fontSize: 13, marginTop: 4, lineHeight: 1.5,
                  overflow: "hidden", display: "-webkit-box",
                  WebkitLineClamp: 2, WebkitBoxOrient: "vertical"
                }}>
                  {applyGermanize(r.snippet)}
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}
